import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import axios from "axios";
import CryptoJS from "crypto-js";
import moment from "moment";

const GraphicDesignerShow = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [designer, setDesigner] = useState(null);
  const [uuid, setUuid] = useState();
  const [stampid, setStampid] = useState();
  const [checksum, setChecksum] = useState();
  const [signing, setSigning] = useState(false); // Execute Contract in progress
  const [downloading, setDownloading] = useState(false); // Download Contract in progress
  const [hasContract, setHasContract] = useState(false); // Download Contract only shows once one exists

  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");

  // Download Contract should only ever be offered once a contract has
  // actually been executed — check on load instead of only discovering
  // "there's nothing to download" after the admin clicks it.
  useEffect(() => {
    if (!currentUser?.user_id) return;
    axios
      .get(`${process.env.REACT_APP_URL}/admin/true_copy`, {
        params: { user_id: currentUser.user_id, limit: 1 },
        headers: { Authorization: `Bearer ${localStorage.getItem("Token")}` },
      })
      .then((res) => {
        setHasContract(Boolean(res.data?.data?.trueCopies?.[0]?.uuid));
      })
      .catch(() => {
        // Silent — this only gates a button's visibility, not a page load.
      });
  }, [currentUser?.user_id]);

  useEffect(() => {
    const fetchDesigner = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_URL}/admin/graphic_designer`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("Token")}`,
            },
          }
        );
        // Find matching designer
        const filteredDesigner = response.data.data.designers.find(
          (designer) => designer.user_id === parseInt(id)
        );

        setDesigner(filteredDesigner);
      } catch (error) {
        console.error("Error fetching graphic designer:", error);
        toast.error("Failed to load graphic designer details");
      } finally {
        setLoading(false);
      }
    };

    fetchDesigner();
  }, [id]);

  if (loading) {
    return (
      <div className="al">
        <div className="al-card" style={{ textAlign: "center", padding: "48px 20px" }}>
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p className="mt-2" style={{ color: "var(--color-text-secondary)" }}>Loading designer details...</p>
        </div>
      </div>
    );
  }

  if (!designer) {
    return (
      <div className="al">
        <div className="al-card" style={{ textAlign: "center", padding: "48px 20px" }}>
          <p style={{ color: "var(--color-text-secondary)" }}>No designer found with the provided ID.</p>
          <button className="al-btn al-btn--primary mt-3" onClick={() => navigate("/admin/graphic-designer")}>
            Back to List
          </button>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });
    } catch {
      return "Invalid Date";
    }
  };

  // Returns { signedWith } on success, or throws with a user-readable message.
  // (Previously fired its own toasts and could end silently when every stamp
  // was already used — the caller now owns all feedback.)
  const getEstampid = async (cs, ts, uuid, currentUser) => {
    const formData = new FormData();
    formData.append("timestamp", ts);
    formData.append("checksum", cs);
    formData.append("initiator", "helpdesk+qamypackgenieapiuser@truecopy.com");

    const res = await axios.post(
      "https://qamypackgenie.truecopy.in/api/gen/v3/getestamplist",
      formData,
      {
        headers: { Authorization: `Bearer ${localStorage.getItem("Token")}` },
      }
    );

    if (!res.data || res.data.length === 0) {
      throw new Error("No e-stamps are available to sign this contract.");
    }

    // Try each available stamp until one signs successfully.
    for (const stamp of res.data) {
      try {
        setStampid(stamp.stampid);

        const csFinal = await generateChecksum(
          process.env.REACT_APP_TRUE_COPY_API_KEY,
          uuid
        );

        await signdocument(csFinal, uuid, stamp.stampid, currentUser);
        return { signedWith: stamp.stampid }; // success
      } catch (err) {
        const msg =
          err.response?.data?.message || err.message || "Unknown error";

        if (
          msg.includes("Estamp invalid / used") ||
          err.response?.data?.errCode === 69
        ) {
          console.warn("Stamp already used, trying next:", stamp.stampid);
          continue;
        }
        throw err;
      }
    }

    // Every stamp was already used — this used to fall through with no message.
    throw new Error(
      "All available e-stamps have already been used. Please add a new e-stamp and try again."
    );
  };

  const signdocument = async (cs, uuid, stampid, currentUser) => {
    const signer = [
      {
        signer: "dev1.webglowx@gmail.com", // `${currentUser.email}`
        signer_name: `${currentUser.name}`,
        signloc: "1[42:36]",
        seq_no: 1,
        parallel_index: 1,
        signer_mobile: "",
        signtype: "AADHAAR_SIGN",
      },
      {
        signer: "dev2.webglowx@gmail.com", // `${user.email}`
        signer_name: `${designer?.name}`,
        signloc: "1[399:46]",
        seq_no: 2,
        parallel_index: 1,
        signer_mobile: "",
        signtype: "AADHAAR_SIGN",
      },
    ];

    const formData = new FormData();
    const response = await fetch("/assets/img/sample.pdf");
    const blob = await response.blob();

    formData.append("uuid", uuid);
    formData.append("cs", cs);
    formData.append("initiator", "helpdesk+qamypackgenieapiuser@truecopy.com");
    formData.append("workflow_id", process.env.REACT_APP_WORKFLOWID);
    formData.append("callbackurl", `${window.location.origin}/admin/dashboard`);
    formData.append("uploadfile", blob, "sample.pdf");
    formData.append("signerlist", JSON.stringify(signer));
    formData.append("estampid", stampid);

    const res = await axios.post(
      "https://qamypackgenie.truecopy.in/services/corpservice/v2/uploadfile",
      formData,
      { headers: { Authorization: `Bearer ${localStorage.getItem("Token")}` } }
    );

    if (res.status !== 200) {
      throw new Error("Upload failed or status not 200");
    }

    return res.data;
  };

  const generateChecksum = async (apiKey, uuid) => {
    if (!apiKey || !uuid) throw new Error("API key or UUID missing");

    const data = new TextEncoder().encode(apiKey + uuid);
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);

    return Array.from(new Uint8Array(hashBuffer))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  };

  // Single entry point for the "Execute Contract" button. It drives all of the
  // on-screen feedback: a loading toast the moment it is clicked, then exactly
  // one definite outcome (success or a specific error). The button previously
  // ran a multi-second, multi-API flow with no indication anything was
  // happening, and several outcomes produced no message at all.
  const executeContract = async () => {
    if (signing) return; // guard: avoid duplicate signings / duplicate emails
    if (!currentUser?.user_id) {
      toast.error("Your session has expired. Please sign in again.");
      return;
    }

    setSigning(true);
    const toastId = toast.loading("Executing contract, please wait…");

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_URL}/admin/true_copy`,
        { user_id: currentUser.user_id },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("Token")}` },
        }
      );

      if (!res.data?.success || !res.data?.data?.uuid) {
        throw new Error(
          res.data?.message || "Could not start the contract workflow."
        );
      }

      const uuid = res.data.data.uuid;
      setUuid(uuid);

      const ts = moment().format("DDMMYYYYHH:mm:ss");
      const cs = CryptoJS.SHA256(
        process.env.REACT_APP_TRUE_COPY_API_KEY + ts
      ).toString(CryptoJS.enc.Hex);

      const { signedWith } = await getEstampid(cs, ts, uuid, currentUser);

      setHasContract(true); // reveal Download Contract without a page refresh

      toast.success(
        `Contract executed successfully. Signing request (e-stamp ${signedWith}) has been sent by email.`,
        { id: toastId, duration: 6000 }
      );
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Something went wrong while executing the contract.";
      console.error("Execute contract failed:", err);
      toast.error(`Contract execution failed: ${msg}`, {
        id: toastId,
        duration: 6000,
      });
    } finally {
      setSigning(false);
    }
  };

  // Looks up whether a contract has actually been executed for this user
  // (createTrueCopy stores one row per Execute Contract run), then fetches
  // the signed PDF from TrueCopy's own gateway (GET .../fetchsignedfile/
  // {uuid}/{cs}, per TrueCopy's "Download Signed File" API — same checksum
  // scheme, sha256(APIKEY+uuid), that signdocument/generateChecksum above
  // already use for uploading) and saves it locally.
  const downloadContract = async () => {
    if (downloading) return;
    if (!currentUser?.user_id) {
      toast.error("Your session has expired. Please sign in again.");
      return;
    }

    setDownloading(true);
    const toastId = toast.loading("Fetching signed contract…");
    try {
      const res = await axios.get(`${process.env.REACT_APP_URL}/admin/true_copy`, {
        params: { user_id: currentUser.user_id, limit: 1 },
        headers: { Authorization: `Bearer ${localStorage.getItem("Token")}` },
      });

      const record = res.data?.data?.trueCopies?.[0];
      if (!record?.uuid) {
        toast.error('No executed contract found yet — run "Execute Contract" first.', {
          id: toastId,
        });
        return;
      }

      const cs = await generateChecksum(
        process.env.REACT_APP_TRUE_COPY_API_KEY,
        record.uuid
      );

      const fileRes = await axios.get(
        `https://qamypackgenie.truecopy.in/services/corpservice/v2/fetchsignedfile/${record.uuid}/${cs}`,
        { responseType: "blob" }
      );

      const blobUrl = URL.createObjectURL(fileRes.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `contract-${record.uuid}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);

      toast.success("Contract downloaded.", { id: toastId });
    } catch (err) {
      // fetchsignedfile 500s both on a genuine error and when the document
      // simply isn't ready yet (a signer hasn't completed their signature)
      // — TrueCopy's own error body (JSON) still comes back as a Blob here
      // because of responseType:"blob", so it has to be read back out.
      const fallbackMsg =
        "The signed document isn't ready yet — both signers need to complete Aadhaar e-sign first.";
      let msg = fallbackMsg;
      try {
        let raw;
        if (err?.response?.data instanceof Blob) {
          const text = await err.response.data.text();
          raw = JSON.parse(text)?.message;
        } else if (err?.response?.data?.message) {
          raw = err.response.data.message;
        } else if (err?.message) {
          raw = err.message;
        }
        // TrueCopy's gateway 500s with the same raw internal exception text
        // (e.g. "Cannot invoke ...Sreq.getStatus() ... is null") whenever the
        // document hasn't finished being signed yet — that's not something an
        // admin can act on, so keep the friendly fallback for it instead.
        if (raw && !/cannot invoke|nullpointer|\bcom\.\w+\./i.test(raw)) {
          msg = raw;
        }
      } catch {
        // Body wasn't readable/JSON — keep the generic message above.
      }
      toast.error(msg, { id: toastId });
    } finally {
      setDownloading(false);
    }
  };

  const initials = (designer?.name || "?").trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase()).join("");

  return (
    <div className="al">
      <div className="al-head">
        <div>
          <h1 className="al-title">Graphic Designer Details</h1>
          <p className="al-sub">View this graphic designer's profile.</p>
        </div>
        <div className="al-head__actions">
          <button
            type="button"
            className="al-btn"
            onClick={() =>
              navigate(`/admin/graphic-designer/edit/${designer.id}`, {
                state: { item: designer },
              })
            }
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z" /></svg>
            Edit
          </button>
          <button type="button" className="al-btn" onClick={() => navigate(-1)}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            Back
          </button>
        </div>
      </div>

      <div className="al-idbanner">
        <div className="al-idbanner__icon">{initials}</div>
        <div>
          <div className="al-idbanner__name">{designer?.name || "N/A"}</div>
          <div className="al-idbanner__sub">{designer?.email || "N/A"}</div>
        </div>
        <span className="al-pill" style={{ marginLeft: "auto", background: designer?.status === "active" ? "var(--color-success-bg)" : "var(--color-surface-2)", color: designer?.status === "active" ? "var(--color-success-text)" : "var(--color-text-secondary)" }}>
          {designer?.status ? designer.status.charAt(0).toUpperCase() + designer.status.slice(1) : "Inactive"}
        </span>
      </div>

      <div className="al-card">
        <div className="al-cardhead">
          <div className="al-cardhead__icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg></div>
          <h3 className="al-cardhead__title">Profile Information</h3>
        </div>
        <div className="al-spec">
          <div>
            <div className="al-spec__lbl">Name</div>
            <div className="al-spec__val">{designer?.name || "N/A"}</div>
          </div>
          <div>
            <div className="al-spec__lbl">Email</div>
            <div className="al-spec__val">{designer?.email || "N/A"}</div>
          </div>
          <div>
            <div className="al-spec__lbl">Phone</div>
            <div className="al-spec__val">{designer?.phone || "N/A"}</div>
          </div>
          <div>
            <div className="al-spec__lbl">Address</div>
            <div className="al-spec__val">{designer?.addresses?.address || "N/A"}</div>
          </div>
          <div>
            <div className="al-spec__lbl">Country</div>
            <div className="al-spec__val">{designer?.addresses?.address || "N/A"}</div>
          </div>
          <div>
            <div className="al-spec__lbl">State</div>
            <div className="al-spec__val">{designer?.addresses?.address || "N/A"}</div>
          </div>
          <div>
            <div className="al-spec__lbl">City</div>
            <div className="al-spec__val">{designer?.addresses?.address || "N/A"}</div>
          </div>
          <div>
            <div className="al-spec__lbl">Pincode</div>
            <div className="al-spec__val">{designer?.addresses?.address || "N/A"}</div>
          </div>
          <div>
            <div className="al-spec__lbl">Status</div>
            <div className="al-spec__val">
              <span className="al-pill" style={{ background: designer?.status === "active" ? "var(--color-success-bg)" : "var(--color-surface-2)", color: designer?.status === "active" ? "var(--color-success-text)" : "var(--color-text-secondary)" }}>
                {designer?.status ? designer.status.charAt(0).toUpperCase() + designer.status.slice(1) : "Inactive"}
              </span>
            </div>
          </div>
          {designer?.created_at && (
            <div>
              <div className="al-spec__lbl">Created At</div>
              <div className="al-spec__val">{formatDate(designer.created_at)}</div>
            </div>
          )}
          {designer?.bio && (
            <div className="al-spec__full">
              <div className="al-spec__lbl">Bio</div>
              <div className="al-spec__val">{designer.bio}</div>
            </div>
          )}
        </div>

        <div className="al-actionbar">
          <span style={{ marginRight: "auto", fontSize: 13, fontWeight: 600, color: "var(--color-text-secondary)" }}>Execute Contract</span>
          <button type="button" className="al-btn al-btn--primary" onClick={executeContract} disabled={signing}
            style={{ opacity: signing ? 0.7 : 1, cursor: signing ? "wait" : "pointer" }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z" /></svg>
            {signing ? "Executing…" : "Sign"}
          </button>
          {hasContract && (
            <button type="button" className="al-btn" onClick={downloadContract} disabled={downloading}
              style={{ opacity: downloading ? 0.7 : 1, cursor: downloading ? "wait" : "pointer" }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
              {downloading ? "Checking…" : "Download Contract"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default GraphicDesignerShow;
